import { AuthGuard, checkPermissions, createFile, useAuth } from "@elit-hair/elit-tools";
import {
    Button,
    Card,
    CardContent,
    ErrorState,
    Icon,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { captureException } from "@sentry/react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { type ColumnDef, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowLeft, TbCalendar, TbDownload, TbExternalLink, TbEye, TbMail, TbPhone, TbRefresh } from "react-icons/tb";
import { toast } from "sonner";
import z from "zod";

import { DataGrid, DataGridContainer, getActionsColumnSize, resizableColumnTableOptions } from "@/components/data-grid";
import { DataGridColumnHeader } from "@/components/data-grid/column-header";
import { DataGridPagination } from "@/components/data-grid/pagination";
import { DataGridTable } from "@/components/data-grid/table";
import { LoadingScreen } from "@/components/loading-screen";
import { OpenRecordButton } from "@/components/ui/open-record-button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { getPredefinedLeadListByKey } from "@/constants/lead-lists.constants";
import { LEAD_BLOCKING_STATE, LEAD_PROCESSING_STATE, type LeadProcessingStateType } from "@/constants/lead.constants";
import { PERMISSIONS } from "@/constants/permissions";
import { useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { useCall } from "@/providers/hooks/use-call";
import {
    LeadWithAssignment,
    useExportLeadsByPriority,
    useLeadsByPriority,
    useRefreshLeadFromZoho,
} from "@/queries/lead.queries";
import { SupportedModuleType } from "@/utils/crm-module.utils";
import { useUrlSyncedTanstackPagination } from "@/utils/pagination";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { getPhoneAppointmentBookingUrl } from "../constants/phone-appointment-booking-urls";
import { getPriorityReportMaxLevel } from "../utils/priority-report.utils";

const SettingsPriorityLeadsParamsSchema = z.object({
    language: z.string(),
    priority: z.string(),
});

const SettingsPriorityLeadsSearchSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_SETTING_PRIORITY_LEADS)({
    component: SettingsPriorityLeads,
    validateSearch: zodValidator(SettingsPriorityLeadsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "setting_priority_leads",
                to: APP_ROUTES_TO.PERFORMANCE_SETTING_PRIORITY_LEADS,
            },
            title: "setting_priority_leads",
        },
    },
    params: {
        parse: (rawParams) => {
            const result = SettingsPriorityLeadsParamsSchema.safeParse(rawParams);

            if (!result.success) {
                throw redirect({
                    to: APP_ROUTES_TO.ROOT,
                    state: {
                        toast: {
                            type: "error",
                            message: "invalid_route_parameters",
                        },
                    },
                });
            }

            return result.data;
        },
        stringify: (params) => ({ language: params.language, priority: params.priority }),
    },
});

export enum REFRESH_STATUS {
    SUCCESS,
    ERROR,
    LOADING,
}

function SettingsPriorityLeads() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });
    const { userData } = useAuth();

    const { language: languageParam, priority: priorityParam } = Route.useParams();
    const { startCall, selectedBrand, isCallActive } = useCall();
    const { page, pageSize, pagination, onPaginationChange, resetPagination } = useUrlSyncedTanstackPagination({
        search,
        navigate,
        to: Route.to,
    });
    const [blockedFilter, setBlockedFilter] = useState<LeadProcessingStateType>(LEAD_PROCESSING_STATE.AVAILABLE);
    const [refreshStatusByLeadId, setRefreshStatusByLeadId] = useState<Record<string, REFRESH_STATUS>>({});

    const priority = useMemo(() => {
        const parsed = parseInt(priorityParam || "0", 10);
        return isNaN(parsed) ? 0 : parsed;
    }, [priorityParam]);

    const maxPriority = useMemo(
        () => getPriorityReportMaxLevel(userData?.permissionCodes),
        [userData?.permissionCodes]
    );

    useEffect(() => {
        if (priority > maxPriority) {
            navigate({ to: APP_ROUTES_TO.PERFORMANCE_SETTING_PRIORITY_REPORT });
        }
    }, [priority, maxPriority, navigate]);

    const listFilter = useMemo(() => getPredefinedLeadListByKey(languageParam || "")?.filter ?? null, [languageParam]);

    useEffect(() => {
        if (!listFilter) {
            navigate({ to: APP_ROUTES_TO.PERFORMANCE_SETTING_PRIORITY_REPORT });
        }
    }, [listFilter, navigate]);

    const getBlockedLeads = useMemo(() => {
        if (blockedFilter === LEAD_PROCESSING_STATE.BLOCKED) return LEAD_BLOCKING_STATE.BLOCKED;
        if (blockedFilter === LEAD_PROCESSING_STATE.AVAILABLE) return LEAD_BLOCKING_STATE.AVAILABLE;
        if (blockedFilter === LEAD_PROCESSING_STATE.AVAILABLE_OR_SOON_RELEASE)
            return LEAD_BLOCKING_STATE.AVAILABLE_OR_SOON_RELEASE;
        return undefined;
    }, [blockedFilter]);

    const canViewLeadId = useMemo(
        () => checkPermissions(userData?.permissionCodes || [], [PERMISSIONS.CALL_SVC_ADMIN_BASE]),
        [userData?.permissionCodes]
    );

    const { mutateAsync: exportLeadsByPriority } = useExportLeadsByPriority();

    const {
        data: leadsResponse,
        isLoading,
        error,
        refetch,
    } = useLeadsByPriority(priority, listFilter, page, pageSize, getBlockedLeads);

    const leads = useMemo(() => leadsResponse?.data || [], [leadsResponse?.data]);

    const assigneeIds = useMemo(
        () => Array.from(new Set(leads.flatMap((lead) => (lead.assigneeId ? [lead.assigneeId] : [])))),
        [leads]
    );
    const { data: batchUsers = [] } = useUsersByIdsBatch(assigneeIds, assigneeIds.length > 0);
    const usersMap = useMemo(() => Object.fromEntries(batchUsers.map((user) => [user.id, user])), [batchUsers]);

    const totalCount = useMemo(() => leadsResponse?.pagination.total || 0, [leadsResponse?.pagination.total]);

    const filterKey = `${priority}-${JSON.stringify(listFilter)}-${getBlockedLeads ?? ""}`;
    const prevFilterKeyRef = useRef<string | null>(null);

    // Reset to page 1 when filters change (skip initial mount so URL page survives reload)
    useEffect(() => {
        if (prevFilterKeyRef.current === null) {
            prevFilterKeyRef.current = filterKey;
            return;
        }

        if (prevFilterKeyRef.current === filterKey) return;

        prevFilterKeyRef.current = filterKey;
        resetPagination();
    }, [filterKey, resetPagination]);

    const handleBack = () => {
        navigate({ to: APP_ROUTES_TO.PERFORMANCE_SETTING_PRIORITY_REPORT });
    };

    const handleViewDetails = useCallback(
        (zohoLeadId: string) => {
            navigate({
                to: APP_ROUTES_TO.RECORD_DETAILS,
                params: { recordId: zohoLeadId, moduleType: SupportedModuleType.LEADS },
            });
        },
        [navigate]
    );

    const { mutateAsync: refreshLeadFromZoho } = useRefreshLeadFromZoho();

    const handleRefreshLeadFromZoho = useCallback(
        (zohoLeadId: string) => {
            setRefreshStatusByLeadId((prev) => ({ ...prev, [zohoLeadId]: REFRESH_STATUS.LOADING }));
            refreshLeadFromZoho(zohoLeadId)
                .then(() => {
                    setRefreshStatusByLeadId((prev) => ({ ...prev, [zohoLeadId]: REFRESH_STATUS.SUCCESS }));
                    toast.success("Lead refreshed from Zoho");
                })
                .catch((error) => {
                    captureException(error);
                    setRefreshStatusByLeadId((prev) => ({ ...prev, [zohoLeadId]: REFRESH_STATUS.ERROR }));
                    toast.error(t("failed_to_refresh_lead_from_zoho"));
                });
        },
        [refreshLeadFromZoho]
    );

    const handleRefreshCurrentPageLeadFromZoho = useCallback(async () => {
        let success = 0;
        for (const lead of leads) {
            setRefreshStatusByLeadId((prev) => ({ ...prev, [lead.zohoLeadId]: REFRESH_STATUS.LOADING }));
            try {
                await refreshLeadFromZoho(lead.zohoLeadId);
                setRefreshStatusByLeadId((prev) => ({ ...prev, [lead.zohoLeadId]: REFRESH_STATUS.SUCCESS }));
                success++;
            } catch {
                setRefreshStatusByLeadId((prev) => ({ ...prev, [lead.zohoLeadId]: REFRESH_STATUS.ERROR }));
                toast.error(t("failed_to_refresh_lead_from_zoho"));
            }
        }
        toast.success(`${success} ${t("leads_refreshed_from_zoho")}`);
    }, [leads, refreshLeadFromZoho]);

    const handleCallClick = useCallback(
        (lead: { phone?: string | null; mobile?: string | null; zohoLeadId: string; orgId: string }) => {
            const phoneNumber = lead.phone || lead.mobile || "";
            startCall(phoneNumber, {
                moduleType: SupportedModuleType.LEADS,
                recordId: lead.zohoLeadId,
                orgId: lead.orgId,
            });
        },
        [startCall]
    );

    const handleOpenPhoneAppointmentBooking = useCallback(() => {
        const bookingUrl = getPhoneAppointmentBookingUrl(languageParam);
        window.open(bookingUrl, "_blank", "noopener,noreferrer");
    }, [languageParam]);

    const handleExportToExcel = useCallback(async () => {
        if (!listFilter) {
            toast.error(t("export_failed"));
            return;
        }

        try {
            const blob = await exportLeadsByPriority({
                priority,
                filter: listFilter,
                blockingState: getBlockedLeads,
            });

            const filename = `setting-priority-leads-p${priority}-${languageParam ?? "all"}.csv`;
            await createFile(blob, filename);
            toast.success(t("export_success"));
        } catch (err) {
            captureException(err);
            toast.error(t("export_failed"));
        }
    }, [exportLeadsByPriority, priority, listFilter, getBlockedLeads, t, languageParam]);

    const columns: ColumnDef<LeadWithAssignment>[] = useMemo(
        () => [
            {
                accessorKey: "settingPriority",
                header: ({ column }) => <DataGridColumnHeader title={t("priority")} column={column} />,
                cell: ({ row }) => {
                    const priority = row.original.settingPriority;
                    return (
                        <div className="flex items-center justify-center">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-sm font-bold text-primary cursor-help">
                                            {priority !== null ? Math.floor(priority / 100) + 1 : "-"}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>
                                            {t("priority")}: {priority !== null ? priority : "-"}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    );
                },
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 80,
                enableHiding: true,
                enableResizing: true,
            },
            {
                accessorKey: "assigneeId",
                header: ({ column }) => <DataGridColumnHeader title={t("assigned")} column={column} />,
                cell: ({ row }) => {
                    const assigneeId = row.original.assigneeId;
                    if (!assigneeId) {
                        return <div className="text-muted-foreground">-</div>;
                    }

                    const assigneeName = usersMap[assigneeId]?.name ?? assigneeId;
                    const assignmentExpiresAt = row.original.assignmentExpiresAt;

                    return (
                        <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{assigneeName}</span>
                            {assignmentExpiresAt ? (
                                <span className="text-xs text-muted-foreground">
                                    {assignmentExpiresAt.toLocaleString(DateTime.DATETIME_SHORT)}
                                </span>
                            ) : null}
                        </div>
                    );
                },
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 180,
                enableHiding: true,
                enableResizing: true,
            },
            {
                accessorKey: "blockedUntil",
                header: ({ column }) => <DataGridColumnHeader title={t("blocked_until")} column={column} />,
                cell: ({ row }) => {
                    const blockedUntil = row.original.blockedUntil;
                    const isStillBlocked = blockedUntil !== null && blockedUntil > DateTime.now();

                    return (
                        <div
                            className={
                                isStillBlocked
                                    ? "text-xs font-medium text-amber-600 dark:text-amber-400"
                                    : "text-xs text-muted-foreground"
                            }
                        >
                            {blockedUntil ? blockedUntil.toLocaleString(DateTime.DATETIME_SHORT) : "-"}
                        </div>
                    );
                },
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 160,
                enableHiding: true,
                enableResizing: true,
            },
            {
                accessorKey: "fullName",
                header: ({ column }) => <DataGridColumnHeader title={t("name")} column={column} />,
                cell: ({ getValue }) => <div className="font-medium">{String(getValue() || "")}</div>,
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 180,
                enableHiding: true,
                enableResizing: true,
            },
            {
                accessorKey: "email",
                header: ({ column }) => <DataGridColumnHeader title={t("email")} column={column} />,
                cell: ({ getValue }) => {
                    const email = getValue<string>();
                    return (
                        <div className="flex items-center gap-2">
                            <Icon icon={TbMail} className="text-muted-foreground shrink-0" />
                            <span>{email || "-"}</span>
                        </div>
                    );
                },
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 260,
                enableHiding: true,
                enableResizing: true,
            },
            {
                accessorKey: "phone",
                header: ({ column }) => <DataGridColumnHeader title={t("phone")} column={column} />,
                cell: ({ row }) => {
                    const phone = row.original.phone || row.original.mobile || "-";
                    return (
                        <div className="flex items-center gap-2">
                            <Icon icon={TbPhone} className="text-muted-foreground shrink-0" />
                            <span>{phone}</span>
                        </div>
                    );
                },
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 150,
                enableHiding: true,
                enableResizing: true,
            },
            {
                accessorKey: "status",
                header: ({ column }) => <DataGridColumnHeader title={t("status")} column={column} />,
                cell: ({ getValue }) => <div className="text-muted-foreground">{String(getValue() || "")}</div>,
                meta: {
                    skeleton: <Skeleton className="w-full h-7" />,
                },
                size: 140,
                enableHiding: true,
                enableResizing: true,
            },
            ...(canViewLeadId
                ? [
                      {
                          accessorKey: "zohoLeadId",
                          header: ({ column }) => <DataGridColumnHeader title={t("id")} column={column} />,
                          cell: ({ row }) => (
                              <div className="font-mono text-xs text-muted-foreground truncate">
                                  {row.original.zohoLeadId || "-"}
                              </div>
                          ),
                          meta: {
                              skeleton: <Skeleton className="w-full h-7" />,
                          },
                          size: 90,
                          enableHiding: true,
                          enableResizing: true,
                      } as ColumnDef<LeadWithAssignment>,
                  ]
                : []),
            {
                id: "actions",
                header: () => <span className="sr-only">{t("actions")}</span>,
                enableResizing: false,
                size: getActionsColumnSize(4, 32),
                minSize: getActionsColumnSize(4, 32),
                maxSize: getActionsColumnSize(4, 32),
                meta: {
                    isActionsColumn: true,
                    skeleton: (
                        <div className="flex gap-2">
                            {Array.from({ length: 3 }).map((_, index) => (
                                <Skeleton key={`skeleton-action-button-${index}`} className="h-8 w-8 rounded" />
                            ))}
                        </div>
                    ),
                },
                cell: ({ row }) => {
                    const lead = row.original;
                    return (
                        <div className="flex w-fit items-center justify-center gap-2">
                            <AuthGuard required={[PERMISSIONS.CALL_SVC_ADMIN_BASE]}>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRefreshLeadFromZoho(lead.zohoLeadId)}
                                    className={
                                        refreshStatusByLeadId[lead.zohoLeadId] === REFRESH_STATUS.SUCCESS
                                            ? "text-green-600 hover:bg-green-100 hover:text-green-700"
                                            : refreshStatusByLeadId[lead.zohoLeadId] === REFRESH_STATUS.ERROR
                                              ? "text-destructive hover:bg-destructive/10 hover:text-destructive"
                                              : refreshStatusByLeadId[lead.zohoLeadId] === REFRESH_STATUS.LOADING
                                                ? "opacity-70"
                                                : undefined
                                    }
                                >
                                    <Icon icon={TbRefresh} />
                                </Button>
                            </AuthGuard>
                            <Button size="sm" variant="ghost" onClick={() => handleViewDetails(lead.zohoLeadId)}>
                                <Icon icon={TbEye} />
                            </Button>
                            {!isCallActive() && (
                                <Button size="sm" variant="ghost" onClick={() => handleCallClick(lead)}>
                                    <Icon icon={TbPhone} />
                                </Button>
                            )}
                            <OpenRecordButton
                                id={lead.zohoLeadId}
                                moduleType={SupportedModuleType.LEADS}
                                brand={selectedBrand ? selectedBrand : undefined}
                                size="sm"
                                label={<Icon icon={TbExternalLink} />}
                            />
                        </div>
                    );
                },
            },
        ],
        [
            t,
            isCallActive,
            selectedBrand,
            handleRefreshLeadFromZoho,
            handleViewDetails,
            handleCallClick,
            refreshStatusByLeadId,
            canViewLeadId,
            usersMap,
        ]
    );

    const table = useReactTable({
        ...resizableColumnTableOptions,
        columns,
        data: leads,
        pageCount: Math.ceil(totalCount / pageSize),
        state: {
            pagination,
            columnPinning: {
                right: ["actions"],
            },
        },
        manualPagination: true,
        onPaginationChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading && leads.length === 0) {
        return <LoadingScreen />;
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-6">
            <div className="mx-auto max-w-7xl space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={handleBack}>
                            <Icon icon={TbArrowLeft} className="mr-2" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold">
                                {t("priority")} {priority} ({languageParam?.toUpperCase()})
                            </h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <p className="text-sm text-muted-foreground">
                            {totalCount} {t("leads_found")}
                        </p>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleOpenPhoneAppointmentBooking}
                            className="gap-2"
                        >
                            <Icon icon={TbCalendar} />
                            {t("phone_appointment")}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleExportToExcel}
                            className="gap-2"
                            data-no-loader
                        >
                            <Icon icon={TbDownload} />
                            {t("export_to_excel")}
                        </Button>
                        <AuthGuard required={[PERMISSIONS.CALL_SVC_ADMIN_BASE]}>
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => handleRefreshCurrentPageLeadFromZoho()}
                            >
                                <Icon icon={TbRefresh} />
                            </Button>
                        </AuthGuard>
                        <Select
                            value={blockedFilter}
                            onValueChange={(value: LeadProcessingStateType) => setBlockedFilter(value)}
                        >
                            <SelectTrigger className="w-[200px]">
                                <SelectValue>
                                    {blockedFilter === LEAD_PROCESSING_STATE.BLOCKED
                                        ? t("blocked")
                                        : blockedFilter === LEAD_PROCESSING_STATE.AVAILABLE_OR_SOON_RELEASE
                                          ? t("available_or_soon_release")
                                          : t("not_blocked")}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={LEAD_PROCESSING_STATE.AVAILABLE}>{t("not_blocked")}</SelectItem>
                                <SelectItem value={LEAD_PROCESSING_STATE.BLOCKED}>{t("blocked")}</SelectItem>
                                <SelectItem value={LEAD_PROCESSING_STATE.AVAILABLE_OR_SOON_RELEASE}>
                                    {t("available_or_soon_release")}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Table Card */}
                <Card className="border-border/40 shadow-sm">
                    <CardContent className="p-0">
                        {error ? (
                            <ErrorState
                                title={t("failed_to_load_leads")}
                                message={error?.message || t("error_loading_leads")}
                                onRetry={refetch}
                            />
                        ) : (
                            <DataGrid
                                table={table}
                                recordCount={totalCount}
                                tableLayout={{
                                    cellBorder: true,
                                    rowBorder: true,
                                    headerBorder: true,
                                    width: "fixed",
                                    columnsResizable: true,
                                    columnsVisibility: true,
                                    columnsPinnable: true,
                                }}
                                isLoading={isLoading}
                                emptyMessage={t("no_leads_available")}
                            >
                                <DataGridContainer>
                                    <ScrollArea>
                                        <DataGridTable />
                                        <ScrollBar orientation="horizontal" />
                                    </ScrollArea>
                                </DataGridContainer>
                                <DataGridPagination />
                            </DataGrid>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
